import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Ban } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import RupiahInput from "@/components/RupiahInput";
import api, { formatDetail } from "@/lib/api";
import { CANCEL_REASON_LABELS, WO_STAGES, deriveWOStage } from "@/lib/constants";
import { todayJKT } from "@/lib/tz";

/**
 * CancelByOwnerDialog — 149-bp. Pembatalan Working Order atas keputusan
 * pemberi kerja (client), BEDA dari `Close` yang menandai pekerjaan selesai.
 * Tidak menghapus data lama; hanya mengunci status terminal `cancelled_by_owner`.
 */
export default function CancelByOwnerDialog({ open, onOpenChange, project, onDone }) {
  const [step, setStep] = useState(1); // 1 = form, 2 = confirm
  const [busy, setBusy] = useState(false);
  const [cancelledDate, setCancelledDate] = useState(todayJKT());
  const [reasonCategory, setReasonCategory] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [incurredCost, setIncurredCost] = useState(0);
  const [billable, setBillable] = useState(false);
  const [billableAmount, setBillableAmount] = useState(0);
  const [ownerConfirmedBy, setOwnerConfirmedBy] = useState("");

  if (!project) return null;
  const stageKey = deriveWOStage(project);
  const stageLabel = WO_STAGES.find((s) => s.key === stageKey)?.label || stageKey;

  const reset = () => {
    setStep(1); setBusy(false);
    setCancelledDate(todayJKT());
    setReasonCategory(""); setReason(""); setNote("");
    setEvidenceUrl(""); setIncurredCost(0);
    setBillable(false); setBillableAmount(0); setOwnerConfirmedBy("");
  };

  const canProceed = cancelledDate && reasonCategory && reason.trim();

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/active-projects/${project.id}/cancel-by-owner`, {
        cancelled_date: cancelledDate,
        reason_category: reasonCategory,
        reason: reason.trim(),
        note: note.trim() || null,
        evidence_url: evidenceUrl.trim() || null,
        incurred_cost: Number(incurredCost) || 0,
        billable: !!billable,
        billable_amount: billable ? (Number(billableAmount) || 0) : 0,
        owner_confirmed_by: ownerConfirmedBy.trim() || null,
      });
      toast.success(`WO ${project.wo_number || project.id.slice(0, 6)} ditandai Dibatalkan Owner. Riwayat & invoice lama tetap tersimpan.`);
      onOpenChange(false);
      reset();
      onDone?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal mencatat pembatalan");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cancel-owner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-rose-700">
            <Ban size={18} /> Batalkan WO oleh Owner
          </DialogTitle>
          <div className="text-xs text-slate-500 mt-1">
            WO <span className="font-semibold text-[#0B1D3A]">{project.wo_number || "(no WO)"}</span> — {project.wo_title || project.project_name || "(no title)"}
          </div>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="overline text-slate-600">Tanggal Pembatalan <span className="text-rose-600 normal-case">*</span></Label>
                <DatePicker testid="cb-date" value={cancelledDate} onChange={setCancelledDate} />
              </div>
              <div className="space-y-1.5">
                <Label className="overline text-slate-600">Tahap Saat Dibatalkan</Label>
                <div className="h-9 border border-slate-200 bg-slate-50 flex items-center px-3 text-sm text-slate-700" data-testid="cb-stage">{stageLabel}</div>
                <p className="text-[9px] text-slate-500">Otomatis dari status WO saat ini.</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Kategori Alasan <span className="text-rose-600 normal-case">*</span></Label>
              <Select value={reasonCategory} onValueChange={setReasonCategory}>
                <SelectTrigger data-testid="cb-reason-category" className="rounded-none h-9 text-sm">
                  <SelectValue placeholder="Pilih kategori…" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {Object.entries(CANCEL_REASON_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`cb-reason-opt-${k}`}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Alasan Pembatalan <span className="text-rose-600 normal-case">*</span></Label>
              <textarea
                data-testid="cb-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A1F]"
                placeholder="Jelaskan alasan pembatalan…"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Catatan Tambahan</Label>
              <textarea
                data-testid="cb-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A1F]"
                placeholder="Konteks tambahan (opsional)…"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="overline text-slate-600">Konfirmasi Pihak Owner</Label>
                <Input
                  data-testid="cb-owner-confirmed"
                  value={ownerConfirmedBy}
                  onChange={(e) => setOwnerConfirmedBy(e.target.value)}
                  placeholder="Nama/PIC dari client…"
                  className="rounded-none h-9 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="overline text-slate-600">Lampiran Bukti (URL)</Label>
                <Input
                  data-testid="cb-evidence-url"
                  value={evidenceUrl}
                  onChange={(e) => setEvidenceUrl(e.target.value)}
                  placeholder="Link ke email/surat pembatalan…"
                  className="rounded-none h-9 text-sm"
                />
              </div>
            </div>

            <div className="border-t border-slate-200 pt-3 space-y-2">
              <div className="overline text-slate-600">Biaya yang Sudah Dikeluarkan</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] text-slate-500">Incurred Cost (Rp)</Label>
                  <RupiahInput testid="cb-incurred-cost" value={incurredCost} onChange={setIncurredCost} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] text-slate-500">Dapat Ditagihkan?</Label>
                  <div className="inline-flex border border-slate-300 h-9">
                    <button type="button" data-testid="cb-billable-no" onClick={() => setBillable(false)}
                      className={`px-3 text-xs uppercase tracking-wider font-semibold ${!billable ? "bg-slate-700 text-white" : "bg-white text-slate-500"}`}>Tidak</button>
                    <button type="button" data-testid="cb-billable-yes" onClick={() => setBillable(true)}
                      className={`px-3 text-xs uppercase tracking-wider font-semibold border-l border-slate-300 ${billable ? "bg-[#FF5A1F] text-white" : "bg-white text-slate-500"}`}>Ya</button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] text-slate-500">Nilai Ditagihkan (Rp)</Label>
                  <RupiahInput testid="cb-billable-amount" value={billableAmount} onChange={setBillableAmount} disabled={!billable} />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Batal</Button>
              <Button
                data-testid="cb-next"
                onClick={() => setStep(2)}
                disabled={!canProceed}
                className="rounded-none bg-rose-700 hover:bg-rose-800 disabled:opacity-50"
              >Lanjut ke Konfirmasi</Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="border-l-4 border-l-rose-500 bg-rose-50 border border-rose-200 p-3 flex items-start gap-2" data-testid="cb-confirm-banner">
              <AlertTriangle size={16} className="text-rose-600 mt-0.5 shrink-0" />
              <div className="text-xs text-rose-800 leading-relaxed">
                WO akan dihentikan dan dikeluarkan dari pekerjaan aktif. Data dan riwayat (invoice, expense, timeline) <b>tidak akan dihapus</b>. Status dapat dibuka kembali oleh user berwenang.
              </div>
            </div>
            <div className="border border-slate-200 bg-white p-3 space-y-1 text-xs">
              <div><span className="overline text-slate-500">Tanggal:</span> <span className="tabular-nums text-slate-800">{cancelledDate}</span></div>
              <div><span className="overline text-slate-500">Tahap:</span> <span className="text-slate-800">{stageLabel}</span></div>
              <div><span className="overline text-slate-500">Kategori:</span> <span className="text-slate-800">{CANCEL_REASON_LABELS[reasonCategory]}</span></div>
              <div><span className="overline text-slate-500">Alasan:</span> <span className="text-slate-800">{reason}</span></div>
              {note && <div><span className="overline text-slate-500">Catatan:</span> <span className="text-slate-800">{note}</span></div>}
              {ownerConfirmedBy && <div><span className="overline text-slate-500">Konfirmasi:</span> <span className="text-slate-800">{ownerConfirmedBy}</span></div>}
              {billable && <div><span className="overline text-slate-500">Nilai Tagihan:</span> <span className="tabular-nums text-slate-800">Rp {Number(billableAmount).toLocaleString("id-ID")}</span></div>}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setStep(1)}>Kembali</Button>
              <Button data-testid="cb-submit" onClick={submit} disabled={busy}
                className="rounded-none bg-rose-700 hover:bg-rose-800 disabled:opacity-50">
                {busy ? "Menyimpan…" : "Konfirmasi Pembatalan"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
